import { forwardRef } from "react";

import { Stack, useTheme } from "@mui/material";

import { FormFieldProps } from "./FormFieldProps";

import { FormFieldLabel } from "@/forms-design-system/FormField/FormFieldLabel";
import { FormTypography } from "@/forms-design-system/FormTypography/FormTypography";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { mergeSx } from "@/helpers/mergeSx";

export const FormField = forwardRef<HTMLDivElement, FormFieldProps>(
  (
    {
      id,
      labelId,
      label,
      infoTooltip,
      helperText,
      error = false,
      required = false,
      children,
      count,
      maxCount,
      disabled,
      components,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();

    if (!theme.palette.forms || !theme.palette.formsComponents?.field) {
      return <InvalidThemeErrorMessage />;
    }

    const hasCount = count !== undefined && maxCount !== undefined;

    return (
      <Stack ref={ref} gap={0.5} width="100%" {...rest}>
        {label && (
          <FormFieldLabel
            id={labelId}
            htmlFor={id}
            label={label}
            infoTooltip={infoTooltip}
            required={required}
            disabled={disabled}
            components={{
              label: components?.label,
              infoTooltip: components?.infoTooltip,
            }}
          />
        )}
        {children}
        {(helperText || hasCount) && (
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent={helperText ? "space-between" : "flex-end"}
            gap={1}
          >
            {helperText && (
              <FormTypography
                flex="1 1 auto"
                variant="caption"
                color={
                  error
                    ? theme.palette.formsComponents.field.errorHelperTextColor
                    : theme.palette.formsComponents.field.helperTextColor
                }
                sx={components?.helperText?.sx}
              >
                {helperText}
              </FormTypography>
            )}
            {hasCount && (
              <FormTypography
                flexShrink={0}
                variant="caption"
                color={theme.palette.formsComponents.field.countTextColor}
                sx={mergeSx({ whiteSpace: "nowrap" }, components?.count?.sx)}
              >
                {count}/{maxCount}
              </FormTypography>
            )}
          </Stack>
        )}
      </Stack>
    );
  }
);

FormField.displayName = "FormField";
export type { FormFieldProps };
